/**
 * Root route. Phase 3c-2 mounts the global `<AppShell>` here so every
 * authenticated route inherits the persistent top bar (live clock,
 * running timer). FU-E adds the global `<Sidebar>` to the LEFT of the
 * shell so brand / nav / notifications / profile all live in one rail
 * across the entire SPA.
 *
 * Auth routes — `/login`, `/signup`, `/forgot-password`, `/reset-password`,
 * `/logout`, `/accept-invite` — render their `<Outlet />` standalone (no
 * shell, no sidebar). The shell calls `useMe()` / `useProjects()` which
 * require a token, so mounting it on the auth routes would either spam
 * 401s or render an empty top bar.
 *
 * Logged-out visitors on a DEPLOYED tenant host are sent to the central
 * auth origin (`login[-staging].3mote.com`) — the one sign-in surface,
 * because Google + Apple only accept a registered origin and `.3mote.com`
 * cookies make its session valid on every tenant — carrying
 * `?return=<tenant>` plus a guarded `?next=<path>` so the deep link they
 * arrived on survives the hop. Localhost keeps its local `/login` (its
 * cookies can't cross to a central origin).
 *
 * Layout shape:
 *
 *   ┌──────────┬──────────────────────────────────┐
 *   │          │           <AppShell> (header)     │
 *   │ Sidebar  ├──────────────────────────────────┤
 *   │          │                                   │
 *   │          │           <Outlet />              │
 *   │          │                                   │
 *   └──────────┴──────────────────────────────────┘
 *
 * The outer `flex h-screen` makes the sidebar full-height; the inner
 * column scrolls independently so the rail stays put while the content
 * area scrolls.
 */

import { Outlet, createRootRoute, useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import { getAccessToken } from '@/auth/cookie-helpers'
import { isCentralAuthHost } from '@/auth/host-parser'
import { agentWorkingStore } from '@/features/agents/agent-working-store'
import { buildCentralLoginUrl } from '@/features/auth/central-redirect'
import { hardReplace } from '@/features/auth/hard-navigate'
import { TimesheetsMenuProvider } from '@/features/time/use-timesheets-menu'
import { AppShell } from '@/layouts/AppShell'
import { Sidebar } from '@/layouts/Sidebar'
import { WebSocketProvider } from '@/lib/websocket-provider'

export const Route = createRootRoute({
  component: RootComponent,
})

const UNAUTHENTICATED_ROUTES = new Set([
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/logout',
  '/accept-invite',
])

/**
 * The paths the central auth origin serves itself; everything else there
 * bounces to `/login`. All five are tenant-free: the password flows are
 * direct browser → Cognito SDK calls, `/signup` is a static gate, and
 * `/logout` runs the global sign-out (which lands back on `/login` here).
 * `/accept-invite` is deliberately absent — an invite link is minted for
 * a tenant host and signs in there.
 */
const CENTRAL_HOST_ROUTES = new Set([
  '/login',
  '/forgot-password',
  '/reset-password',
  '/signup',
  '/logout',
])

function RootComponent() {
  const location = useLocation()
  const navigate = useNavigate()
  const isAuthRoute = UNAUTHENTICATED_ROUTES.has(location.pathname)
  const hasToken = getAccessToken() != null

  // Signed out by any route (expired refresh, 401 chain, sign-out): leases from
  // the previous session must not glow for whoever signs in next on this page.
  useEffect(() => {
    if (!hasToken) agentWorkingStore.clear()
  }, [hasToken])

  // Central auth origin (`login{-staging}.3mote.com` — the one origin
  // Google + Apple accept; see docs/auth-flow.md): only the tenant-free
  // auth surfaces in CENTRAL_HOST_ROUTES render. The authenticated shell
  // must never mount here — there is no tenant to resolve, so `useMe()`
  // etc. would be meaningless. Every other path hard-redirects to
  // `/login`, preserving the raw query string (`?return=` / `?next=` must
  // survive; a router `navigate` would run the target's `validateSearch`
  // against whatever the CURRENT route considers valid, so the plain
  // `location.replace` is the byte-safe way to carry arbitrary params
  // across).
  const isCentralHost = typeof window !== 'undefined' && isCentralAuthHost(window.location.host)
  const centralNeedsRedirect = isCentralHost && !CENTRAL_HOST_ROUTES.has(location.pathname)
  useEffect(() => {
    if (centralNeedsRedirect) {
      window.location.replace(`/login${window.location.search}`)
    }
  }, [centralNeedsRedirect])

  // On an authenticated route with no token, leave BEFORE the shell
  // mounts. `<Sidebar>` + `<AppShell>` fire useMe() / useProjects() /
  // usePresence() / useConversations() / useRunningTimer() / useWikiTree() on
  // mount; letting them run unauthenticated spams the network tab with
  // speculative 401s and flashes broken chrome. The home route has the same
  // guard for its own render, but the shell wraps the Outlet so the gate has
  // to live one level up here too. (Guarded off on the central host —
  // the redirect effect above owns navigation there.)
  //
  // Deployed tenant hosts go straight to the central auth origin — the
  // one sign-in surface — carrying `?return=<tenant>` plus a guarded
  // `?next=` so the deep link the visitor arrived on survives the hop;
  // `hardReplace` so Back never re-lands on a page that only re-bounces.
  // Localhost / unknown hosts have no central twin (`buildCentralLoginUrl`
  // → null) and keep the local `/login` page. Bare `/` carries no `next`:
  // the tenant origin already lands there.
  useEffect(() => {
    if (isCentralHost || isAuthRoute || hasToken) return
    const { pathname, search, hash } = window.location
    // A bare `/` carries nothing worth a hop, but `/?selectedDeliverable=…`
    // (a master-board card modal) is a real deep link — keep the query/hash.
    const next = pathname === '/' && !search && !hash ? undefined : `${pathname}${search}${hash}`
    const centralUrl = buildCentralLoginUrl(window.location.host, { next })
    if (centralUrl) {
      hardReplace(centralUrl)
    } else {
      navigate({ to: '/login' })
    }
  }, [isCentralHost, isAuthRoute, hasToken, navigate])

  // Central host, off-allow-list path: render nothing while the hard
  // redirect above lands. The allow-listed paths fall through to the
  // normal auth-route branch below (all are in UNAUTHENTICATED_ROUTES),
  // where `/login`'s route component picks the central experience
  // host-side.
  if (centralNeedsRedirect) {
    return null
  }

  // App-wide OS-file drag guard. A file dropped on any surface without its
  // own drop handling (a page header, the right rail, an empty route…)
  // makes the browser NAVIGATE to the file, destroying all SPA state.
  // preventDefault-ing window-level dragover + drop for file drags is the
  // standard SPA guard; surface-level drop zones layer on top unaffected
  // (their handlers run first in the bubble phase). Native file inputs are
  // skipped so dropping onto an `<input type="file">` keeps working.
  // Full pattern: docs/os-file-drag-and-drop.md.
  useEffect(() => {
    const isFileDrag = (event: globalThis.DragEvent) => {
      const types = event.dataTransfer?.types
      if (!types) return false
      return Array.from(types as ArrayLike<string>).includes('Files')
    }
    const guard = (event: globalThis.DragEvent) => {
      if (!isFileDrag(event)) return
      const target = event.target
      if (target instanceof Element && target.closest('input[type="file"]')) return
      event.preventDefault()
    }
    window.addEventListener('dragover', guard)
    window.addEventListener('drop', guard)
    return () => {
      window.removeEventListener('dragover', guard)
      window.removeEventListener('drop', guard)
    }
  }, [])

  if (isAuthRoute) {
    return (
      <div className="min-h-full">
        <Outlet />
      </div>
    )
  }

  // No token → render nothing while the effect above redirects. Returning
  // null here is what actually prevents the bootstrap-query 401 spam — the
  // shell components never mount.
  if (!hasToken) {
    return null
  }

  // `<WebSocketProvider>` owns the single shared WS connection for this
  // tab. It must wrap every realtime consumer — the four hooks today
  // (useGlobalRealtime, useChatGlobalRealtime, useChatRealtime,
  // useRealtimeBoard) all mount inside Sidebar / AppShell / route Outlet,
  // so wrapping the authenticated tree is the right scope. Auth-only
  // routes above bypass the provider, which is correct — no token, no
  // socket to open.
  return (
    <TimesheetsMenuProvider>
      <WebSocketProvider>
        {/* `overflow-hidden` on the outer shell is load-bearing: without it,
            a tall page (e.g. the Settings → My Profile tab with all its
            preference cards) busts past `h-screen` and triggers window-
            level scrolling. The sidebar (which is in the document flow
            with `h-screen`) then visually drifts up with the rest of the
            page. Clipping here forces every scroll to happen inside the
            inner `<main overflow-auto>`, keeping the sidebar fixed. */}
        <div className="flex h-screen overflow-hidden">
          <Sidebar />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <AppShell>
              <main className="min-h-0 flex-1 overflow-auto">
                <Outlet />
              </main>
            </AppShell>
          </div>
        </div>
      </WebSocketProvider>
    </TimesheetsMenuProvider>
  )
}
